import { smartQueue } from '../utils/queue';
import { EventPayload } from '../analytics';
import { getTenantNamespace } from '@/app-config';
export const CHRONOSPHERE_LOGS_INGEST_ENDPOINT = '/api/unstable/data/logs/forward';
export const CLOUD_UI_LOGGING_SERVICE = 'cloud-ui-browser';
type LogPayload = Record<string, unknown>;

type SessionData = {
  sessionId: string;
  fullstorySessionUrl: string;
};

/**
 * Client for sending logs to Chronosphere.
 * @TODO: This more or less adheres to the https://getanalytics.io/plugins/writing-plugins/
 * interface which is something we might want to consider using in the future.
 */
export class ChronosphereLogsClient {
  url: string;
  service: string;
  identifyPayload: EventPayload;
  private _logQueue: ReturnType<typeof smartQueue<LogPayload>>;
  private _enabled: boolean;
  private _lastPathname: string | undefined;
  private _sessionData: SessionData | undefined;
  private _preIdentityBuffer: LogPayload[];

  constructor(url?: string) {
    this.url = url || CHRONOSPHERE_LOGS_INGEST_ENDPOINT;
    this.service = CLOUD_UI_LOGGING_SERVICE;
    this.identifyPayload = {};
    this._enabled = false;
    this._preIdentityBuffer = [];

    this._logQueue = smartQueue<LogPayload>(
      (events) => {
        this._flushLogs(events);
      },
      {
        max: 10, // limit
        interval: 3000, // 3s
        throttle: true, // Ensure only max is processed at interval
      }
    );
  }

  /**
   * Log a page view event.
   */
  page() {
    if (this._lastPathname === window.location.pathname) {
      return;
    }
    const pathname = window.location.pathname;
    this.submitLog({
      message: '[page_view] ' + pathname,
      event_name: 'page_view',
      previous_page_url: this._lastPathname,
    });
    this._lastPathname = pathname;
  }

  /**
   * Identify a user.
   */
  identify(userId: string, payload: EventPayload) {
    this._enabled = true;
    this.identifyPayload = {
      user_id: userId,
      ...payload,
    };

    // TODO: Remove email from logs if it's not a Chronosphere email until its safe to do so.
    if (typeof this.identifyPayload.email === 'string' && !this.identifyPayload.email.includes('@chronosphere.io')) {
      delete this.identifyPayload.email;
    }

    // Replay any buffered events that were captured before identity was established
    // Enrich them with the now-populated identifyPayload
    if (this._preIdentityBuffer.length > 0) {
      this._preIdentityBuffer.forEach((log) => {
        this._logQueue.push({
          ...log,
          ...this.identifyPayload,
        });
      });
      this._preIdentityBuffer = [];
    }

    // Log page view once we have the user info
    this.page();
  }

  /**
   * Track an event.
   */
  track(eventName: string, payload?: object) {
    this.submitLog({
      message: '[event] ' + eventName,
      event_name: eventName,
      ...payload,
    });
  }

  /**
   * Submit a log event to the server. Batches the event with other logs.
   */
  submitLog(log: LogPayload) {
    const tenantName = getTenantNamespace();
    const payload = {
      happened_at: new Date().toISOString(),
      ...log,
      ...this.identifyPayload,
      ...this._getPageInfo(),
      ...this._sessionData,

      'resource.type': 'browser',
      // @chao mentioned we should dual write this field since we optimize the index on this.
      'resource.labels.namespace_name': tenantName,
      tenant_name: tenantName,

      service: this.service,
      level: log.level ?? 'info',
    };

    this._logQueue.push(payload);
  }

  setSessionData(data: SessionData) {
    this._sessionData = data;
  }

  /**
   * Flush a list of logs to the server.
   */
  _flushLogs(logs: LogPayload[]) {
    // Don't process logs in development
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    // Buffer logs captured before user identity is established (e.g., RUM events during page load)
    if (!this._enabled) {
      this._preIdentityBuffer.push(...logs);
      return;
    }

    const payload = logs.map((e) => JSON.stringify(e)).join('\n');
    try {
      fetch(this.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      });
    } catch (e) {
      console.error('Failed to send logs to Chronosphere', e);
    }
  }

  /**
   * Get information about the current page.
   */
  _getPageInfo() {
    return {
      page_title: document.title,
      page_url: window.location.href,
    };
  }
}

export const chronosphereLogsClient = new ChronosphereLogsClient();
